import _ from 'lodash'
import RetencySlider from './RetencySlider'
import { useState, useEffect, useMemo } from 'react'
import { InfoIcon, TimeIcon } from '@/components/icons'
import useAudienceTargetingStore from '@/store/audienceTargetingStore'
import { AUDIENCE_CREATION_SETTING_NOTE } from '@/utils/ad-campaign-constants'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

const FacebookFansSettings = ({ stage, audience, selectedAudience, readOnly = false }) => {
    const { getAudienceSettings, updateAudienceSetting } = useAudienceTargetingStore()

    const [recencyValue, setRecencyValue] = useState(365)

    // Get current settings
    const audienceSettings = getAudienceSettings(stage, audience)
    const storedRecency = audienceSettings?.recency_days

    // Default recency is 365 days
    const defaultRecency = 365

    // Create debounced update function
    const debouncedUpdate = useMemo(
        () => _.debounce((value) => {
            updateAudienceSetting(stage, audience, 'recency_days', value)
        }, 300),
        [stage, audience, updateAudienceSetting]
    )

    // Initialize recency from stored value or default
    useEffect(() => {
        if (readOnly) return
        if (storedRecency !== undefined) {
            // Accept any value between 0-365 (continuous)
            const validValue = Math.min(Math.max(storedRecency, 0), 365)
            setRecencyValue(validValue)
            if (validValue !== storedRecency) {
                updateAudienceSetting(stage, audience, 'recency_days', validValue)
            }
        } else if (selectedAudience?.name === 'Facebook Fans' && stage === 'reengagement') {
            // Auto-populate default recency if no value set and this is Facebook Fans
            setRecencyValue(defaultRecency)
            updateAudienceSetting(stage, audience, 'recency_days', defaultRecency)
        }
    }, [storedRecency, selectedAudience, stage, audience, defaultRecency, updateAudienceSetting, readOnly])

    // Proactively set default when component mounts for Facebook Fans (ensures consistency)
    useEffect(() => {
        if (readOnly) return
        if (storedRecency === undefined && selectedAudience?.name === 'Facebook Fans' && stage === 'reengagement') {
            updateAudienceSetting(stage, audience, 'recency_days', defaultRecency)
        }
    }, [selectedAudience, stage, audience, storedRecency, defaultRecency, updateAudienceSetting, readOnly])

    // Only show settings for Facebook Fans reengagement audiences
    if (selectedAudience?.name !== 'Facebook Fans' || stage !== 'reengagement') {
        return null
    }

    // Format recency label for display
    const formatRecencyLabel = (days) => `0-${days} days`

    const handleValueChange = (value) => {
        setRecencyValue(value)
        debouncedUpdate(value)
    }

    return (
        <div className="tw:flex tw:gap-6 tw:flex-col tw:bg-secondary-background tw:rounded-xs">
            <div className="tw:flex tw:gap-1.5 tw:items-center tw:p-3 tw:pb-0">
                <span className="tw:text-gray-200">
                    <TimeIcon className="tw:w-6 tw:h-6" />
                </span>
                <span className="tw:font-semibold">Recency</span>
                <Tooltip>
                    <TooltipTrigger>
                        <InfoIcon className="tw:w-5 tw:h-5 tw:text-gray-200" />
                    </TooltipTrigger>
                    <TooltipContent showArrow={false}>
                        <div className="tw:text-sm tw:text-gray-400 tw:max-w-[220px]">
                            Target users who engaged with your Facebook page within the selected time period.
                            <br />
                            <span className="tw:text-white tw:font-medium tw:block tw:mt-2">Default: 365 days</span>
                        </div>
                    </TooltipContent>
                </Tooltip>
            </div>
            <div className="tw:px-3 tw:pb-3">
                <div className="tw:flex tw:flex-col tw:gap-2">
                    {/* Recency Slider */}
                    <div className="tw:relative tw:py-1">
                        <div className="tw:text-sm tw:flex tw:gap-1.5">
                            <span className="tw:text-gray-200">Selected Range</span>
                            <b className="tw:text-semibold">
                                {formatRecencyLabel(readOnly ? (storedRecency ?? defaultRecency) : recencyValue)}
                            </b>
                        </div>
                        {readOnly ? (
                            <p className="tw:mt-2.5 tw:text-sm tw:text-gray-200" data-testid="recency-read-only-note">
                                {AUDIENCE_CREATION_SETTING_NOTE}
                            </p>
                        ) : (
                            <div className="tw:mt-2.5">
                                <RetencySlider value={recencyValue} onChange={handleValueChange} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default FacebookFansSettings
